import API from '@/APIs/API';
import {ParameterStore} from '@/Core/mobx/ParameterStore/ParameterStore';

export const annuityAPI = async (parameterStore: ParameterStore) => {
    const {quote, prequal} = parameterStore;

    const age = prequal.age.value;
    const monthlyPremium = quote.monthlyPremiumAnnuity.value;
    const singlePremium = quote.singlePremiumAnnuity.value;
    const allocation = quote.allocationAnnuity.valueAsString();

    try {
        const data = await API.Call<{quotes: any[]; cursor: string}>(
            `v2/internal/quotes/annuity?age=${age}&monthlyPremium=${monthlyPremium}&singlePremium=${singlePremium}&allocation=${encodeURIComponent(allocation)}`,
            'GET',
            undefined,
        );
        if (!data || !data.quotes || data.quotes.length === 0) {
            return null;
        }
        const annuityQuote = data.quotes[0];
        console.log('Annuity quote data:', annuityQuote);

        const {'1-M': toMaturity, '1-10': to10Years} = annuityQuote;
        const cashValueYearsToMaturity = toMaturity.filter((item: any) =>
            [60, 65, 70, 75, 80, 85].includes(item.beginningOfYearAge),
        );
        const cashValueYearsTo10Years = to10Years.filter((item: any) =>
            [60, 65, 70, 75, 80, 85].includes(item.beginningOfYearAge),
        );

        const out = [
            {
                name: 'Carrier',
                value: 'National Life Group',
                type: 'string',
            },
            {
                name: 'Product',
                value: 'Annuity',
                type: 'string',
            },
            {
                name: 'Product name',
                value: quote.productAnnuity.valueAsString(),
                type: 'string',
            },
            {
                name: 'Annual premium',
                value: monthlyPremium * 12,
                type: 'currency',
            },
            {
                name: 'Monthly premium',
                value: monthlyPremium,
                type: 'currency',
            },
            {
                name: 'Single premium',
                value: singlePremium,
                type: 'currency',
            },
            {
                name: 'Premium allocation',
                value: allocation,
                type: 'string',
            },
            age <= 60 && {
                name: 'Cash value at age 60 (applied premium to maturity)',
                value: cashValueYearsToMaturity.find((item: any) => item.beginningOfYearAge === 60)?.cashValue,
                type: 'currency',
            },
            age <= 65 && {
                name: 'Cash value at age 65 (applied premium to maturity)',
                value: cashValueYearsToMaturity.find((item: any) => item.beginningOfYearAge === 65)?.cashValue,
                type: 'currency',
            },
            age <= 70 && {
                name: 'Cash value at age 70 (applied premium to maturity)',
                value: cashValueYearsToMaturity.find((item: any) => item.beginningOfYearAge === 70)?.cashValue,
                type: 'currency',
            },
            age <= 75 && {
                name: 'Cash value at age 75 (applied premium to maturity)',
                value: cashValueYearsToMaturity.find((item: any) => item.beginningOfYearAge === 75)?.cashValue,
                type: 'currency',
            },
            age <= 80 && {
                name: 'Cash value at age 80 (applied premium to maturity)',
                value: cashValueYearsToMaturity.find((item: any) => item.beginningOfYearAge === 80)?.cashValue,
                type: 'currency',
            },
            age <= 85 && {
                name: 'Cash value at age 85 (applied premium to maturity)',
                value: cashValueYearsToMaturity.find((item: any) => item.beginningOfYearAge === 85)?.cashValue,
                type: 'currency',
            },
            age <= 60 && {
                name: 'Cash value at age 60 (applied premium for 10 years)',
                value: cashValueYearsTo10Years.find((item: any) => item.beginningOfYearAge === 60)?.cashValue,
                type: 'currency',
            },
            age <= 65 && {
                name: 'Cash value at age 65 (applied premium for 10 years)',
                value: cashValueYearsTo10Years.find((item: any) => item.beginningOfYearAge === 65)?.cashValue,
                type: 'currency',
            },
            age <= 70 && {
                name: 'Cash value at age 70 (applied premium for 10 years)',
                value: cashValueYearsTo10Years.find((item: any) => item.beginningOfYearAge === 70)?.cashValue,
                type: 'currency',
            },
            age <= 75 && {
                name: 'Cash value at age 75 (applied premium for 10 years)',
                value: cashValueYearsTo10Years.find((item: any) => item.beginningOfYearAge === 75)?.cashValue,
                type: 'currency',
            },
            age <= 80 && {
                name: 'Cash value at age 80 (applied premium for 10 years)',
                value: cashValueYearsTo10Years.find((item: any) => item.beginningOfYearAge === 80)?.cashValue,
                type: 'currency',
            },
            age <= 85 && {
                name: 'Cash value at age 85 (applied premium for 10 years)',
                value: cashValueYearsTo10Years.find((item: any) => item.beginningOfYearAge === 85)?.cashValue,
                type: 'currency',
            },
            {
                name: 'Cash value at age (applied premium to maturity)',
                value: cashValueYearsToMaturity.map((item: any) => item.cashValue),
                xAxis: cashValueYearsToMaturity.map((item: any) => item.beginningOfYearAge),
                type: 'year',
            },
            {
                name: 'Cash value at age (applied premium for 10 years)',
                value: cashValueYearsTo10Years.map((item: any) => item.cashValue),
                xAxis: cashValueYearsTo10Years.map((item: any) => item.beginningOfYearAge),
                type: 'year',
            },
            {
                name: 'Accumulation value at age (applied premium to maturity)',
                value: cashValueYearsToMaturity.map((item: any) => item.accumulationValue),
                xAxis: cashValueYearsToMaturity.map((item: any) => item.beginningOfYearAge),
                type: 'year',
            },
            {
                name: 'Accumulation value at age (applied premium for 10 years)',
                value: cashValueYearsTo10Years.map((item: any) => item.accumulationValue),
                xAxis: cashValueYearsTo10Years.map((item: any) => item.beginningOfYearAge),
                type: 'year',
            },
            {
                name: 'Death benefit at age (applied premium to maturity)',
                value: cashValueYearsToMaturity.map((item: any) => item.deathBenefit),
                xAxis: cashValueYearsToMaturity.map((item: any) => item.beginningOfYearAge),
                type: 'year',
            },
            {
                name: 'Death benefit at age (applied premium for 10 years)',
                value: cashValueYearsTo10Years.map((item: any) => item.deathBenefit),
                xAxis: cashValueYearsTo10Years.map((item: any) => item.beginningOfYearAge),
                type: 'year',
            },
            {
                name: 'Valid',
                value: 'valid',
                type: 'string',
            },
        ].filter((item) => item);

        const returnData = {
            faceAmount: 'N/A',
            monthlyPremium: monthlyPremium,
            productName: quote.productAnnuity.valueAsString(),
            out: out,
        };

        console.log('Annuity return data:', returnData);
        return returnData;
    } catch (error) {
        console.error('Failed to load annuity quote. Please try again.');
        return null;
    }
};
